/**
 * Room domain types - single source of truth for room enums.
 * These are shared between backend and frontends.
 */
import type {
  RoomType as PrismaRoomType,
  PlaylistDestination as PrismaPlaylistDestination,
  SupportedLanguagesEnum,
  DefaultImageGenerationStatus,
} from "../../service/prisma/client.ts";
import type prisma from "../../service/prisma/index.ts";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// WIRE PORTABILITY (read-flip vague 2)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

type PgRoomRow = Awaited<ReturnType<typeof prisma.room.findUniqueOrThrow>>;

/**
 * The room columns that the admin and member routes send. An ALLOW-LIST, as
 * `PUBLIC_ROOM_CARD_KEYS` is for the public card (`router/webapp/room-access.ts`):
 * a column added to `Room` reaches no response until someone adds it here. The
 * member routes drop more keys from it (`enrichRoomsWithRelations`).
 *
 * Not here: `resourcesOrder`, which only the "All" routes read
 * (`domain/room-resources-order.ts`), and up to 7.8 kB of it for one room.
 */
const WIRE_ROOM_KEYS = [
  "id",
  "translations",
  "type",
  "organisation",
  "isPublic",
  "discoverable",
  "discoveryFilter",
  "createdAt",
  "updatedAt",
  "backgroundColor",
  "primaryColor",
  "secondaryColor",
  "mainSpecialty",
  "medicalSpecialties",
  "tags",
  "professions",
  "acceptanceCriteria",
  "allowedLanguages",
  "audiences",
  "topics",
  "facets",
  "filterTopics",
  "sharingQrCodeId",
  "marketingAssets",
  "ico",
  "apiKey",
  "congressTab",
  "resourcesTab",
  "requireMedicalIdentifier",
  "privacyMarketingSettings",
  "termsConditions",
  "meshDescriptors",
  "clientProfile",
  "isActive",
  "activeCampaignListId",
  "ogImage",
  "ogImageGenerationStatus",
  "relatedRooms",
  "offLabelCheck",
] as const;

type WireRoomKey = (typeof WIRE_ROOM_KEYS)[number];

/**
 * READ-FLIPPED (vague 2): the PG room row's enum scalars (`type`,
 * `allowedLanguages`, `ogImageGenerationStatus`) are typed via the PG client,
 * which the frontends cannot name → TS2883 declaration-emit portability error.
 * Re-express exactly those three via the Mongo client enums (the wire's lingua
 * franca, nameable from every frontend); the values are identical string
 * unions, so the assignment is structural and lossless. Everything else
 * (scalars + JSONB via the global `PrismaJson` namespace) is already portable.
 */
export type WireRoomRow = Omit<
  Pick<PgRoomRow, WireRoomKey>,
  "type" | "allowedLanguages" | "ogImageGenerationStatus"
> & {
  type: PrismaRoomType;
  allowedLanguages: SupportedLanguagesEnum[];
  ogImageGenerationStatus: DefaultImageGenerationStatus | null;
};

/**
 * Narrow a room row to `WIRE_ROOM_KEYS`, and re-express the enum scalars for
 * wire portability. Written out key by key, so the compiler proves the result
 * carries nothing else.
 */
export function toWireRoom(row: PgRoomRow): WireRoomRow {
  return {
    id: row.id,
    translations: row.translations,
    type: row.type,
    organisation: row.organisation,
    isPublic: row.isPublic,
    discoverable: row.discoverable,
    discoveryFilter: row.discoveryFilter,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    backgroundColor: row.backgroundColor,
    primaryColor: row.primaryColor,
    secondaryColor: row.secondaryColor,
    mainSpecialty: row.mainSpecialty,
    medicalSpecialties: row.medicalSpecialties,
    tags: row.tags,
    professions: row.professions,
    acceptanceCriteria: row.acceptanceCriteria,
    allowedLanguages: row.allowedLanguages,
    audiences: row.audiences,
    topics: row.topics,
    facets: row.facets,
    filterTopics: row.filterTopics,
    sharingQrCodeId: row.sharingQrCodeId,
    marketingAssets: row.marketingAssets,
    ico: row.ico,
    apiKey: row.apiKey,
    congressTab: row.congressTab,
    resourcesTab: row.resourcesTab,
    requireMedicalIdentifier: row.requireMedicalIdentifier,
    privacyMarketingSettings: row.privacyMarketingSettings,
    termsConditions: row.termsConditions,
    meshDescriptors: row.meshDescriptors,
    clientProfile: row.clientProfile,
    isActive: row.isActive,
    activeCampaignListId: row.activeCampaignListId,
    ogImage: row.ogImage,
    ogImageGenerationStatus: row.ogImageGenerationStatus,
    relatedRooms: row.relatedRooms,
    offLabelCheck: row.offLabelCheck,
  };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ROOM TYPE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Re-exported from @prisma/client as type alias */
export type RoomType = PrismaRoomType;

/**
 * Enum-like const with Title case keys matching Prisma values.
 * Exhaustive - TS error if Prisma enum changes.
 */
export const RoomType = {
  Default: "Default",
  ExternalApi: "ExternalApi",
  // UPPERCASE aliases for webapp backward compatibility
  DEFAULT: "Default",
  EXTERNAL_API: "ExternalApi",
} as const satisfies Record<string, RoomType>;

export const ROOM_TYPES: RoomType[] = ["Default", "ExternalApi"];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// PLAYLIST DESTINATION
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Re-exported from @prisma/client as type alias */
export type PlaylistDestination = PrismaPlaylistDestination;

/**
 * Enum-like const with both Title case and UPPERCASE keys.
 * Exhaustive - TS error if Prisma enum changes.
 */
export const PlaylistDestination = {
  // Title case keys (admin uses ContentDestinationEnum.Congress)
  Discovery: "discovery",
  Congress: "congress",
  Resources: "resources",
  // UPPERCASE keys (webapp uses EventDisplayDestination.DISCOVERY)
  DISCOVERY: "discovery",
  CONGRESS: "congress",
  RESOURCES: "resources",
} as const satisfies Record<string, PlaylistDestination>;

export const PLAYLIST_DESTINATIONS: PlaylistDestination[] = [
  "discovery",
  "congress",
  "resources",
];

/** Alias for backward compatibility - webapp uses EventDisplayDestination */
export const EventDisplayDestination = PlaylistDestination;
export type EventDisplayDestination = PlaylistDestination;

/** Alias for backward compatibility - admin uses ContentDestinationEnum */
export const ContentDestinationEnum = PlaylistDestination;
export type ContentDestinationEnum = PlaylistDestination;

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ELIGIBILITY
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** The acceptance axis that kept a user out — one axis, never a set. */
export type RoomEligibilityCriterion = "country" | "profession" | "specialty";

/**
 * Why a user may or may not enter a Room.
 *
 * A discriminated union rather than a boolean because one caller needs the
 * REASON: JuisciGPT drops a Room that matched on evidence but fails
 * eligibility, and correctly excluding a well-matched HCP is an invisible
 * commercial loss. `juiscigpt_room_suppressed` is what makes it countable, and
 * it can only carry a blocking criterion the checker actually reports.
 *
 * Returning the reason from the SINGLE shared helper is also what keeps the
 * in-memory pre-filter and the `join/check` authority from diverging: both read
 * the same criteria object through this function, so a room the pre-filter
 * hides is exactly a room the join would refuse.
 */
export type RoomEligibility =
  | { status: "eligible" }
  | { status: "blocked"; criterion: RoomEligibilityCriterion };

/**
 * Evaluate a Room's acceptance criteria against a user profile.
 *
 * An absent `acceptanceCriteria`, or an empty array on an axis, means NO
 * restriction on that axis — most rooms restrict nothing.
 *
 * When several axes block at once, the first in evaluation order (country →
 * profession → specialty) is the one reported: the eligibility review needs one
 * attributable reason per suppression, not a set. A restricted axis the user
 * has no value for (a country-gated room, a user with no country) blocks —
 * missing data cannot satisfy a restriction.
 */
export function roomEligibility(
  room: {
    acceptanceCriteria: {
      acceptedCountries: string[];
      acceptedProfessions: string[];
      acceptedMedicalSpecialties: string[];
    } | null;
  },
  user: {
    country: string | null;
    profession: string | null;
    main_specialty: string | null;
    medical_specialties: string[];
  },
): RoomEligibility {
  if (room.acceptanceCriteria === null) return { status: "eligible" };

  const { acceptedCountries, acceptedProfessions, acceptedMedicalSpecialties } =
    room.acceptanceCriteria;

  if (
    acceptedCountries.length > 0 &&
    (user.country === null || !acceptedCountries.includes(user.country))
  )
    return { status: "blocked", criterion: "country" };

  if (
    acceptedProfessions.length > 0 &&
    (user.profession === null || !acceptedProfessions.includes(user.profession))
  )
    return { status: "blocked", criterion: "profession" };

  const userSpecialties = [
    ...user.medical_specialties,
    ...(user.main_specialty === null ? [] : [user.main_specialty]),
  ];

  if (
    acceptedMedicalSpecialties.length > 0 &&
    !userSpecialties.some((specialty) =>
      acceptedMedicalSpecialties.includes(specialty),
    )
  )
    return { status: "blocked", criterion: "specialty" };

  return { status: "eligible" };
}

/**
 * Whether a user satisfies the room's professional record requirement.
 *
 * Kept apart from `roomEligibility` on purpose. The acceptance criteria answer
 * "does this profile match", and `join/check` reports that verdict as a bare
 * boolean the webapp uses to offer the join. The record is something the
 * reader can supply AT the door (the record modal), so a missing one must not
 * hide the join; it must stop the join, and stop the surfaces that PROPOSE the
 * room. Members are never asked: callers apply this to non-members only.
 */
export function meetsRecordRequirement(
  room: { requireMedicalIdentifier: boolean | null },
  hasProfessionalRecord: boolean,
): boolean {
  return room.requireMedicalIdentifier !== true || hasProfessionalRecord;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SCREENSHOT CAPTURE TARGETS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * The room pages an automated capture may target.
 *
 * ONE-WAY CONTRACT with the webapp's route table. Every section here MUST be a
 * `ROOM_ROUTES` key whose pattern is exactly `RoomCaptureRoutePattern` — the
 * shape `roomCapturePath` builds below. `roomIdFromPath.ts` pins that at compile
 * time. The reverse does NOT hold and must not: the webapp adds room routes
 * freely, and a new route is never forced into this enum.
 *
 * Reshape a capture route on the webapp side (to `/rooms/…`, or to
 * `/room/:roomId/:section`) and that pin stops compiling, which is the signal to
 * move `roomCapturePath` with it rather than to discover it from a blank image.
 */
export const ROOM_CAPTURE_SECTIONS = ["discovery", "resources"] as const;
export type RoomCaptureSection = (typeof ROOM_CAPTURE_SECTIONS)[number];

/** The route SHAPE a capture targets. The webapp pins ROOM_ROUTES against it. */
export type RoomCaptureRoutePattern<S extends RoomCaptureSection> =
  `/room/${S}/:roomId`;

/**
 * The webapp path a capture renders, in an explicit language.
 *
 * `language` is NOT nullable, and that is the point. Without `?lang=`, `LangSync`
 * falls back to normal resolution: the interface locale follows the signed-in
 * user's stored `language` (for a capture, the screenshot service account's — an
 * arbitrary `fr`), while the corpus follows `resolveRoomLanguage`, which can land
 * on a different language entirely when the account's own is outside the room's
 * `allowedLanguages`. The result is a capture with French chrome over English
 * content, which is not publishable and which no caller wants. With `?lang=`,
 * `LangSync` forces all three axes — interface, corpus, and the room's own text —
 * so the only representable capture is a coherent one.
 */
export function roomCapturePath(
  section: RoomCaptureSection,
  roomId: string,
  language: SupportedLanguagesEnum,
): string {
  return `/room/${section}/${roomId}?lang=${language}`;
}
